import type { MockDatabase, PermissionMatrix, Role, RoleCode } from "@/types/domain";
import { sessionApi } from "@/api/session";
import { api } from "@/api/index";

/** Frontend-only snapshot (deep clone from API). Not the mock backend store. */
let snapshot: MockDatabase | null = null;
let sessionRole: RoleCode | string = "sdk_admin";
let sessionProfile: Role | null = null;
let sessionPermissions: PermissionMatrix | null = null;
let inflight: Promise<MockDatabase> | null = null;

function deepClone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

function recomputeStats(db: MockDatabase) {
  db.GLOBAL_STATS.top_games = db.GAMES.filter((g) => g.status === "active");
  db.GLOBAL_STATS.channel_summary = db.CHANNEL_COMPARE;
  db.GLOBAL_STATS.total.games = db.GAMES.length;
  db.GLOBAL_STATS.total.packages = db.PACKAGES.length;
}

export async function refreshAppStore(_force = false): Promise<MockDatabase> {
  if (inflight) return inflight;
  inflight = (async () => {
    const [{ data }, session] = await Promise.all([api.bootstrap(), sessionApi.get()]);
    snapshot = deepClone(data);
    sessionRole = session.data.currentRole;
    sessionProfile = session.data.role;
    sessionPermissions = session.data.permissions;
    snapshot.SESSION.currentRole = sessionRole as RoleCode;
    recomputeStats(snapshot);
    return snapshot;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function getAppStore(): MockDatabase {
  if (!snapshot) throw new Error("App store not hydrated. Call refreshAppStore() first.");
  return snapshot;
}

export function DB(): MockDatabase {
  return getAppStore();
}

export async function ensureAppStore(): Promise<MockDatabase> {
  if (snapshot) return snapshot;
  return refreshAppStore(true);
}

export function getSessionState() {
  return {
    currentRole: sessionRole,
    role: sessionProfile,
    permissions: sessionPermissions,
  };
}

export async function switchRole(role: string) {
  await sessionApi.setRole(role);
  await refreshAppStore(true);
}

export async function applySessionPayload(session: {
  data: { currentRole: string; role: Role | null; permissions: PermissionMatrix | null };
}) {
  sessionRole = session.data.currentRole;
  sessionProfile = session.data.role;
  sessionPermissions = session.data.permissions;
  if (snapshot) snapshot.SESSION.currentRole = sessionRole as RoleCode;
}

export async function invalidateAndRefresh() {
  snapshot = null;
  return refreshAppStore(true);
}

/** Test helper: clear frontend cache without refetching. */
export function resetAppStore() {
  snapshot = null;
  sessionRole = "sdk_admin";
  sessionProfile = null;
  sessionPermissions = null;
  inflight = null;
}

/**
 * Frontend cache accessor for pages.
 * Prefer typed API modules for mutations, then call invalidateAndRefresh().
 */
export const MOCK: MockDatabase = new Proxy({} as MockDatabase, {
  get(_t, prop: string | symbol) {
    return getAppStore()[prop as keyof MockDatabase];
  },
  set(_t, prop: string | symbol, value) {
    const store = getAppStore();
    (store as unknown as Record<string | symbol, unknown>)[prop] = value;
    return true;
  },
});

declare global {
  interface Window {
    MOCK: MockDatabase;
  }
}

if (typeof window !== "undefined") {
  Object.defineProperty(window, "MOCK", {
    configurable: true,
    get() {
      return MOCK;
    },
  });
}
